import { FC } from "react";
import { IconSvgElement } from "@hugeicons/react";
import {
  CircleIcon,
  HourglassIcon,
  Loading03Icon,
  MinusSignCircleIcon,
  MinusSignIcon,
  PlayCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-solid-rounded";
import {
  FaceID,
  Home02,
  ShoppingCart01,
  SVGComponentProps,
  User03,
  Wallet02
} from "untitledui-js-base";
import { ApplicantStepKey, ApplicantStepState } from "api/applications";

export const STEPS: {
  key: ApplicantStepKey;
  label: string;
  short: string;
  Icon: FC<SVGComponentProps>;
}[] = [
  {
    key: "rental_application",
    label: "Rental application",
    short: "Rental app",
    Icon: User03
  },
  {
    key: "housing_history",
    label: "Housing history",
    short: "Housing",
    Icon: Home02
  },
  {
    key: "identity_verification",
    label: "Identity verification",
    short: "Identity",
    Icon: FaceID
  },
  {
    key: "income_verification",
    label: "Income history",
    short: "Income",
    Icon: Wallet02
  },
  {
    key: "checkout",
    label: "Checkout",
    short: "Checkout",
    Icon: ShoppingCart01
  }
];

export const STEP_STATE_LABEL: Record<ApplicantStepState, string> = {
  done: "Done",
  progress: "In progress",
  processing: "Processing",
  todo: "Not started",
  manual: "Awaiting manual run",
  skipped: "Wasn't needed",
  na: "Not required"
};

export const STEP_STATE_ICON: Record<
  ApplicantStepState,
  { icon: IconSvgElement; className: string; motion?: string }
> = {
  done: { icon: CheckmarkCircle02Icon, className: "text-primary-500" },
  progress: {
    icon: Loading03Icon,
    className: "text-primary-300",
    motion: "phc-spin"
  },
  processing: {
    icon: HourglassIcon,
    className: "text-primary-400",
    motion: "phc-tilt"
  },
  todo: { icon: CircleIcon, className: "text-content-200" },
  manual: { icon: PlayCircleIcon, className: "text-primary-500" },
  skipped: { icon: MinusSignCircleIcon, className: "text-content-200" },
  na: { icon: MinusSignIcon, className: "text-content-200" }
};

export const LEGEND_ORDER: ApplicantStepState[] = [
  "done",
  "progress",
  "processing",
  "todo",
  "manual",
  "skipped",
  "na"
];

export const GRID_CLASS_NAME =
  "grid grid-cols-[minmax(0,1fr)_repeat(5,60px)_116px_84px_32px] items-center gap-x-1";

export const OPEN_DELAY_MS = 250;
export const CLOSE_DELAY_MS = 300;

export const MOTION_CSS = `
@keyframes phc-card-in { from { opacity: 0; transform: translateX(-8px) scale(0.98); } to { opacity: 1; transform: none; } }
@keyframes phc-row-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes phc-pop { 0% { opacity: 0; transform: scale(0.3); } 60% { opacity: 1; transform: scale(1.18); } 100% { transform: scale(1); } }
@keyframes phc-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes phc-spin { to { transform: rotate(360deg); } }
@keyframes phc-tilt { 0%, 40% { transform: rotate(0); } 50%, 90% { transform: rotate(180deg); } 100% { transform: rotate(360deg); } }
.phc-card { transform-origin: left top; animation: phc-card-in 180ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
.phc-row { animation: phc-row-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both; animation-delay: var(--phc-row-delay); }
.phc-cell { animation: phc-fade 160ms ease-out both; animation-delay: var(--phc-cell-delay); }
.phc-cell-done { animation: phc-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both; animation-delay: var(--phc-cell-delay); }
.phc-spin { animation: phc-spin 2.4s linear infinite; }
.phc-tilt { animation: phc-tilt 2.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .phc-card, .phc-row, .phc-cell, .phc-cell-done, .phc-spin, .phc-tilt { animation: none; }
}
`;
